import type { SurfaceId } from '../sim/types';
import { SURFACE_INDEX } from '../sim/surfaces';
import type { CenterlinePoint, RoadStageSpec, Track } from './types';

/** Centripetal Catmull-Rom through the control points, resampled every `step` metres of arc. */
export function buildTrack(spec: RoadStageSpec, step = 1): Track {
  const cp = spec.centerline;
  // 1. Dense polyline from the spline (fixed parameter steps per segment).
  const dense: CenterlinePoint[] = [];
  const P = (i: number) => cp[Math.max(0, Math.min(cp.length - 1, i))];
  for (let i = 0; i < cp.length - 1; i++) {
    const p0 = i === 0 ? extrapolate(cp[0], cp[1]) : P(i - 1);
    const p1 = P(i);
    const p2 = P(i + 1);
    const p3 = i + 2 < cp.length ? P(i + 2) : extrapolate(cp[cp.length - 1], cp[cp.length - 2]);
    const segLen = Math.hypot(p2.x - p1.x, p2.z - p1.z);
    const n = Math.max(2, Math.ceil(segLen / 0.25));
    for (let k = 0; k < n; k++) dense.push(catmull(p0, p1, p2, p3, k / n));
  }
  dense.push({ ...cp[cp.length - 1] });

  // 2. Arc-length parametrisation and resampling.
  const cum = new Float64Array(dense.length);
  for (let i = 1; i < dense.length; i++) cum[i] = cum[i - 1] + Math.hypot(dense[i].x - dense[i - 1].x, dense[i].z - dense[i - 1].z);
  const length = cum[dense.length - 1];
  const n = Math.floor(length / step) + 1;
  const t: Track = {
    step,
    length,
    s: new Float64Array(n),
    x: new Float64Array(n),
    y: new Float64Array(n),
    z: new Float64Array(n),
    tx: new Float64Array(n),
    tz: new Float64Array(n),
    width: new Float64Array(n),
    camber: new Float64Array(n),
    curvature: new Float64Array(n),
    surface: new Uint8Array(n),
  };
  let j = 0;
  for (let i = 0; i < n; i++) {
    const s = i * step;
    while (j < dense.length - 2 && cum[j + 1] < s) j++;
    const seg = cum[j + 1] - cum[j];
    const u = seg > 1e-9 ? (s - cum[j]) / seg : 0;
    const a = dense[j];
    const b = dense[j + 1];
    t.s[i] = s;
    t.x[i] = a.x + (b.x - a.x) * u;
    t.y[i] = a.y + (b.y - a.y) * u;
    t.z[i] = a.z + (b.z - a.z) * u;
    t.width[i] = a.width + (b.width - a.width) * u;
    t.camber[i] = a.camber + (b.camber - a.camber) * u;
  }
  // 3. Tangents and signed curvature (+ = left) from smoothed headings.
  const heading = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const a = Math.max(0, i - 1);
    const b = Math.min(n - 1, i + 1);
    const dx = t.x[b] - t.x[a];
    const dz = t.z[b] - t.z[a];
    const l = Math.hypot(dx, dz) || 1;
    t.tx[i] = dx / l;
    t.tz[i] = dz / l;
    heading[i] = Math.atan2(dx, dz);
  }
  // Unwrap heading.
  for (let i = 1; i < n; i++) {
    let d = heading[i] - heading[i - 1];
    while (d > Math.PI) d -= 2 * Math.PI;
    while (d < -Math.PI) d += 2 * Math.PI;
    heading[i] = heading[i - 1] + d;
  }
  const w = Math.max(1, Math.round(3 / step));
  for (let i = 0; i < n; i++) {
    const a = Math.max(0, i - w);
    const b = Math.min(n - 1, i + w);
    t.curvature[i] = (heading[b] - heading[a]) / ((b - a) * step || 1);
  }
  // 4. Surfaces along the centreline (base ranges, then patches on top).
  const base = SURFACE_INDEX.gravel;
  t.surface.fill(base);
  const paint = (from: number, to: number, sid: SurfaceId) => {
    for (let i = Math.max(0, Math.floor(from / step)); i <= Math.min(n - 1, Math.ceil(to / step)); i++) t.surface[i] = SURFACE_INDEX[sid];
  };
  for (const r of spec.surfaces) paint(r.from, r.to, r.surface);
  for (const r of spec.patches) paint(r.from, r.to, r.surface);
  return t;
}

function extrapolate(a: CenterlinePoint, b: CenterlinePoint): CenterlinePoint {
  return { x: 2 * a.x - b.x, z: 2 * a.z - b.z, y: 2 * a.y - b.y, width: a.width, camber: a.camber };
}

function catmull(p0: CenterlinePoint, p1: CenterlinePoint, p2: CenterlinePoint, p3: CenterlinePoint, u: number): CenterlinePoint {
  // Centripetal parametrisation (alpha = 0.5) in the horizontal plane; y/width/camber follow.
  const d = (a: CenterlinePoint, b: CenterlinePoint) => Math.pow(Math.hypot(b.x - a.x, b.z - a.z) + 1e-6, 0.5);
  const t0 = 0;
  const t1 = t0 + d(p0, p1);
  const t2 = t1 + d(p1, p2);
  const t3 = t2 + d(p2, p3);
  const t = t1 + (t2 - t1) * u;
  const lerp = (a: number, b: number, ta: number, tb: number) => (tb - ta === 0 ? a : ((tb - t) / (tb - ta)) * a + ((t - ta) / (tb - ta)) * b);
  const f = (k: 'x' | 'z' | 'y' | 'width' | 'camber') => {
    const a1 = lerp(p0[k], p1[k], t0, t1);
    const a2 = lerp(p1[k], p2[k], t1, t2);
    const a3 = lerp(p2[k], p3[k], t2, t3);
    const b1 = lerp(a1, a2, t0, t2);
    const b2 = lerp(a2, a3, t1, t3);
    return lerp(b1, b2, t1, t2);
  };
  return { x: f('x'), z: f('z'), y: f('y'), width: f('width'), camber: f('camber') };
}

/**
 * Spatial index over track samples for nearest-point queries (stage builder, reset-to-road,
 * progress, bots). Uses a uniform grid of 16 m cells.
 */
export class TrackIndex {
  readonly track: Track;
  private readonly cell = 16;
  private readonly cells = new Map<number, number[]>();

  constructor(track: Track) {
    this.track = track;
    for (let i = 0; i < track.s.length; i++) {
      const k = this.key(Math.floor(track.x[i] / this.cell), Math.floor(track.z[i] / this.cell));
      let arr = this.cells.get(k);
      if (!arr) this.cells.set(k, (arr = []));
      arr.push(i);
    }
  }

  private key(cx: number, cz: number): number {
    return (cx + 32768) * 65536 + (cz + 32768);
  }

  /** Index of the nearest sample within `radius` metres (−1 if none). */
  nearest(x: number, z: number, radius = 48): number {
    const t = this.track;
    const r = Math.ceil(radius / this.cell);
    const cx = Math.floor(x / this.cell);
    const cz = Math.floor(z / this.cell);
    let best = -1;
    let bestD = radius * radius;
    for (let i = -r; i <= r; i++) {
      for (let j = -r; j <= r; j++) {
        const arr = this.cells.get(this.key(cx + i, cz + j));
        if (!arr) continue;
        for (const k of arr) {
          const dx = t.x[k] - x;
          const dz = t.z[k] - z;
          const d = dx * dx + dz * dz;
          if (d < bestD) {
            bestD = d;
            best = k;
          }
        }
      }
    }
    return best;
  }

  /** Nearest sample searching only near a previous index (fast tracking of a moving car). */
  nearestNear(x: number, z: number, hint: number, window = 60): number {
    const t = this.track;
    const a = Math.max(0, hint - window);
    const b = Math.min(t.s.length - 1, hint + window);
    let best = hint;
    let bestD = Infinity;
    for (let k = a; k <= b; k++) {
      const dx = t.x[k] - x;
      const dz = t.z[k] - z;
      const d = dx * dx + dz * dz;
      if (d < bestD) {
        bestD = d;
        best = k;
      }
    }
    return best;
  }

  /** Continuous projection: arc length s and signed lateral offset d (+ = left) of (x, z). */
  project(x: number, z: number, hint = -1, out = { s: 0, d: 0, i: 0 }): { s: number; d: number; i: number } {
    const t = this.track;
    let i = hint >= 0 ? this.nearestNear(x, z, hint) : this.nearest(x, z, 200);
    if (i < 0) i = 0;
    // Refine within the segment toward the neighbour.
    const dx = x - t.x[i];
    const dz = z - t.z[i];
    const along = dx * t.tx[i] + dz * t.tz[i];
    const s = t.s[i] + along;
    // Left normal of tangent (tx, tz) is (tz, -tx)? Forward (sin h, cos h) ⇒ left = (cos h, -sin h).
    const d = dx * t.tz[i] - dz * t.tx[i];
    out.s = Math.max(0, Math.min(t.length, s));
    out.d = d;
    out.i = i;
    return out;
  }
}
